import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus,
  BookOpen,
  Loader2,
  ChevronDown,
  ChevronUp,
  Pencil,
  Trash2,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

interface Props {
  baProjectId: string | null;
}

interface Decision {
  id: string;
  title: string;
  decision_text: string;
  rationale: string;
  alternatives: string;
  decided_by: string;
  decided_at: string;
  status: "open" | "made" | "reversed";
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  open: "bg-blue-100 text-blue-800 border-blue-300",
  made: "bg-green-100 text-green-800 border-green-300",
  reversed: "bg-gray-100 text-gray-600 border-gray-300",
};

const EMPTY_FORM = {
  title: "",
  decision_text: "",
  rationale: "",
  alternatives: "",
  decided_by: "",
  decided_at: "",
  status: "open" as "open" | "made" | "reversed",
};

export default function DecisionLogPanel({ baProjectId }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    if (baProjectId) load();
    else setDecisions([]);
  }, [baProjectId]);

  const load = async () => {
    if (!baProjectId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE}/api/tools/decisions?project_id=${baProjectId}`,
        { headers: authHeader }
      );
      setDecisions(res.data || []);
    } catch (e) {
      console.error("Failed to load decisions", e);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
  };

  const openEdit = (d: Decision) => {
    setEditingId(d.id);
    setForm({
      title: d.title,
      decision_text: d.decision_text,
      rationale: d.rationale,
      alternatives: d.alternatives,
      decided_by: d.decided_by,
      decided_at: d.decided_at ? d.decided_at.split("T")[0] : "",
      status: d.status,
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(
          `${BASE}/api/tools/decisions/${editingId}`,
          form,
          { headers: authHeader }
        );
      } else {
        await axios.post(
          `${BASE}/api/tools/decisions`,
          { ...form, project_id: baProjectId },
          { headers: authHeader }
        );
      }
      setShowForm(false);
      setForm({ ...EMPTY_FORM });
      setEditingId(null);
      load();
    } catch (e) {
      console.error("Failed to save decision", e);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await axios.delete(`${BASE}/api/tools/decisions/${id}`, {
        headers: authHeader,
      });
      setDeleteConfirmId(null);
      load();
    } catch (e) {
      console.error("Failed to delete decision", e);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (!baProjectId) {
    return (
      <div className="h-full flex items-center justify-center text-[#5C5C57]">
        <p className="text-sm">No project selected.</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-[#314E6E]" />
          <h2 className="text-lg font-semibold text-[#1E1E1C]">Decision Log</h2>
          <span className="text-xs text-[#5C5C57] bg-[#F7F4EE] border border-[#D9D4CC] rounded-full px-2 py-0.5">
            {decisions.length}
          </span>
        </div>
        <Button
          size="sm"
          onClick={openNew}
          className="bg-[#314E6E] hover:bg-[#243b54] text-white gap-1"
        >
          <Plus className="h-4 w-4" />
          Add Decision
        </Button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-[#314E6E]" />
        </div>
      )}

      {/* Empty state */}
      {!loading && decisions.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-[#5C5C57]">
          <BookOpen className="h-10 w-10 mb-3 opacity-30" />
          <p className="text-sm font-medium">No decisions logged yet.</p>
          <p className="text-xs mt-1 opacity-70">
            Track key decisions and their rationale here.
          </p>
        </div>
      )}

      {/* Decision list */}
      {!loading && decisions.length > 0 && (
        <div className="space-y-3">
          {decisions.map((d) => {
            const expanded = expandedId === d.id;
            return (
              <div
                key={d.id}
                className="bg-[#FCFBF8] border border-[#D9D4CC] rounded-xl p-4"
              >
                {/* Card header */}
                <div
                  className="flex items-start gap-3 cursor-pointer"
                  onClick={() => toggleExpand(d.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-medium text-[#1E1E1C] text-sm leading-snug">
                        {d.title}
                      </span>
                      <Badge
                        variant="outline"
                        className={`text-xs capitalize ${STATUS_COLORS[d.status]}`}
                      >
                        {d.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#5C5C57] flex-wrap">
                      {d.decided_by && <span>By: {d.decided_by}</span>}
                      {d.decided_at && (
                        <span>
                          {new Date(d.decided_at).toLocaleDateString("en-AU", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      )}
                    </div>
                    {!expanded && d.rationale && (
                      <p className="text-xs text-[#5C5C57] mt-1.5 line-clamp-2">
                        {d.rationale}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(d);
                      }}
                      className="p-1.5 rounded-lg hover:bg-[#F7F4EE] text-[#5C5C57] hover:text-[#1E1E1C] transition-colors"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirmId(d.id);
                      }}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-[#5C5C57] hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    {expanded ? (
                      <ChevronUp className="h-4 w-4 text-[#5C5C57]" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-[#5C5C57]" />
                    )}
                  </div>
                </div>

                {/* Expanded details */}
                {expanded && (
                  <div className="mt-4 space-y-3 border-t border-[#D9D4CC] pt-4">
                    {d.decision_text && (
                      <div>
                        <p className="text-xs font-semibold text-[#314E6E] uppercase tracking-wide mb-1">
                          Decision
                        </p>
                        <p className="text-sm text-[#1E1E1C] whitespace-pre-wrap">
                          {d.decision_text}
                        </p>
                      </div>
                    )}
                    {d.rationale && (
                      <div>
                        <p className="text-xs font-semibold text-[#314E6E] uppercase tracking-wide mb-1">
                          Rationale
                        </p>
                        <p className="text-sm text-[#5C5C57] whitespace-pre-wrap">
                          {d.rationale}
                        </p>
                      </div>
                    )}
                    {d.alternatives && (
                      <div>
                        <p className="text-xs font-semibold text-[#314E6E] uppercase tracking-wide mb-1">
                          Alternatives Considered
                        </p>
                        <p className="text-sm text-[#5C5C57] whitespace-pre-wrap">
                          {d.alternatives}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg bg-[#FCFBF8] border border-[#D9D4CC]">
          <DialogHeader>
            <DialogTitle className="text-[#1E1E1C]">
              {editingId ? "Edit Decision" : "Log a Decision"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Title <span className="text-red-500">*</span>
              </label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Decision title"
                className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Decision
              </label>
              <Textarea
                value={form.decision_text}
                onChange={(e) =>
                  setForm({ ...form, decision_text: e.target.value })
                }
                placeholder="What was decided?"
                rows={3}
                className="border-[#D9D4CC] bg-white text-[#1E1E1C] resize-none"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Rationale
              </label>
              <Textarea
                value={form.rationale}
                onChange={(e) =>
                  setForm({ ...form, rationale: e.target.value })
                }
                placeholder="Why was this decision made?"
                rows={3}
                className="border-[#D9D4CC] bg-white text-[#1E1E1C] resize-none"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Alternatives Considered
              </label>
              <Textarea
                value={form.alternatives}
                onChange={(e) =>
                  setForm({ ...form, alternatives: e.target.value })
                }
                placeholder="What alternatives were considered?"
                rows={2}
                className="border-[#D9D4CC] bg-white text-[#1E1E1C] resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                  Decided By
                </label>
                <Input
                  value={form.decided_by}
                  onChange={(e) =>
                    setForm({ ...form, decided_by: e.target.value })
                  }
                  placeholder="Name or role"
                  className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                  Date
                </label>
                <Input
                  type="date"
                  value={form.decided_at}
                  onChange={(e) =>
                    setForm({ ...form, decided_at: e.target.value })
                  }
                  className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Status
              </label>
              <Select
                value={form.status}
                onValueChange={(v) =>
                  setForm({ ...form, status: v as typeof form.status })
                }
              >
                <SelectTrigger className="border-[#D9D4CC] bg-white text-[#1E1E1C]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="made">Made</SelectItem>
                  <SelectItem value="reversed">Reversed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowForm(false)}
              className="border-[#D9D4CC] text-[#5C5C57]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving || !form.title.trim()}
              className="bg-[#314E6E] hover:bg-[#243b54] text-white"
            >
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {editingId ? "Save Changes" : "Log Decision"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog
        open={!!deleteConfirmId}
        onOpenChange={() => setDeleteConfirmId(null)}
      >
        <DialogContent className="max-w-sm bg-[#FCFBF8] border border-[#D9D4CC]">
          <DialogHeader>
            <DialogTitle className="text-[#1E1E1C]">Delete Decision?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5C5C57] mt-1">
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteConfirmId(null)}
              className="border-[#D9D4CC] text-[#5C5C57]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => deleteConfirmId && remove(deleteConfirmId)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
